/**
 * Execution History Store (Zustand)
 * Manages execution history loading and pagination
 *
 * State includes:
 * - Array of past execution records
 * - Loading state
 * - Error state
 * - Pagination (offset, limit)
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { processThreadExecutionApiClient } from '@flow-studio/api';
import type { ExecutionHistoryItem } from '@flow-studio/types';

// ============================================================================
// INTERFACES
// ============================================================================

interface ExecutionHistoryState {
  // Data
  history: ExecutionHistoryItem[];
  total: number;
  offset: number;
  limit: number;

  // Loading State
  isLoading: boolean;
  error: string | null;

  // Actions
  loadHistory: (
    processThreadId: number,
    offset?: number,
    limit?: number
  ) => Promise<void>;
  loadMore: () => Promise<void>;
  clearHistory: () => void;
  setError: (error: string | null) => void;
}

// ============================================================================
// ZUSTAND STORE
// ============================================================================

export const useExecutionHistoryStore = create<ExecutionHistoryState>()(
  devtools(
    (set, get) => ({
      // ========================================================================
      // INITIAL STATE
      // ========================================================================
      history: [],
      total: 0,
      offset: 0,
      limit: 20,
      isLoading: false,
      error: null,

      // ========================================================================
      // ACTIONS
      // ========================================================================

      /**
       * Load execution history from API
       * Fetches paginated list of past executions
       */
      loadHistory: async (processThreadId, offset = 0, limit = 20) => {
        set({ isLoading: true, error: null });

        try {
          // Convert offset-based pagination to page-based (PageNumber is 1-indexed)
          const pageNumber = Math.floor(offset / limit) + 1;
          const response = await processThreadExecutionApiClient.search({
            processThreadId,
            pageNumber,
            pageSize: limit,
          });

          if (response.success && response.data) {
            const historyItems: ExecutionHistoryItem[] = response.data;

            set({
              history:
                offset === 0
                  ? historyItems
                  : [...get().history, ...historyItems],
              total: response.metadata?.totalCount ?? historyItems.length,
              offset,
              limit,
              isLoading: false,
            });
          } else {
            set({
              error:
                response.error?.message ||
                'Failed to load execution history',
              isLoading: false,
            });
          }
        } catch (error) {
          const errorMsg =
            error instanceof Error ? error.message : 'Unknown error';
          console.error('[ExecutionHistoryStore] Load history error:', errorMsg);
          set({
            error: errorMsg,
            isLoading: false,
          });
        }
      },

      /**
       * Load next page of history
       * Appends to existing history array
       */
      loadMore: async () => {
        const { offset, limit, history } = get();

        // Don't load if no history yet
        if (history.length === 0) return;

        // Get processThreadId from first item
        const processThreadId = history[0]?.processThreadId;
        if (processThreadId) {
          await get().loadHistory(processThreadId, offset + limit, limit);
        }
      },

      /**
       * Clear all history from store
       */
      clearHistory: () => {
        set({
          history: [],
          total: 0,
          offset: 0,
          error: null,
        });
      },

      /**
       * Set error message
       */
      setError: (error) => {
        set({ error });
      },
    }),
    { name: 'ExecutionHistoryStore' }
  )
);

// ============================================================================
// EXPORTS
// ============================================================================

export type { ExecutionHistoryState };
